import { useEffect, useMemo, useState, useCallback } from "react";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { Plus, MapPin, ExternalLink, Pencil, Trash2, Flag } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { RegataForm } from "@/components/RegataForm";
import { RegateImport } from "@/components/RegateImport";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, parseDay, todayStr, BOAT_CATEGORIES, slug } from "@/lib/api";

const RegataCard = ({ r, staff, onEdit, onDelete }) => (
  <div className="fade-up bg-white border border-slate-200 rounded-xl p-6 grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-5" data-testid={`regata-card-${r.id}`}>
    <div className="sm:border-r sm:pr-5">
      <div className="font-heading text-3xl font-bold text-[#0B192C] leading-none">{fmtDate(r.data_inizio).slice(0, 5)}</div>
      <div className="text-xs text-muted-foreground mt-1">al {fmtDate(r.data_fine)}</div>
      <Badge variant="outline" className="mt-3">{r.tipo}</Badge>
    </div>
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          {r.denominazione && <h3 className="font-heading text-2xl uppercase leading-tight" data-testid={`regata-name-${r.id}`}>{r.denominazione}</h3>}
          <div className={r.denominazione ? "text-base font-semibold flex items-center gap-1.5 mt-1" : "font-heading text-2xl uppercase flex items-center gap-2"}><MapPin className="h-4 w-4 text-red-600" />{r.luogo}</div>
          <p className="text-sm text-slate-600">{r.club_numero && `Circolo n.${r.club_numero} · `}{r.club_nome}{r.club_indirizzo && ` · ${r.club_indirizzo}`}</p>
        </div>
        {staff && (
          <div className="flex gap-1 shrink-0">
            <Button size="icon" variant="ghost" onClick={() => onEdit(r)} data-testid={`regata-edit-button-${r.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="text-red-600" onClick={() => onDelete(r)} data-testid={`regata-delete-button-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">{r.categorie_barca.map((c) => <Badge key={c} className="bg-[#0B192C]">{c}</Badge>)}</div>
      {r.data_termine_iscrizioni && <p className="text-sm mt-3"><span className="text-muted-foreground">Termine iscrizioni:</span> <strong>{fmtDate(r.data_termine_iscrizioni)}</strong></p>}
      {r.nota && <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{r.nota}</p>}
      <div className="flex flex-wrap gap-4 mt-3 text-sm">
        {r.link_iscrizioni && <a href={r.link_iscrizioni} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline flex items-center gap-1" data-testid={`regata-signup-link-${r.id}`}>Iscrizioni<ExternalLink className="h-3.5 w-3.5" /></a>}
        {r.link_albo && <a href={r.link_albo} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline flex items-center gap-1" data-testid={`regata-board-link-${r.id}`}>Albo regata<ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>
    </div>
  </div>
);

export default function Regate() {
  const { user } = useAuth();
  const staff = ["admin", "collaboratore"].includes(user.role);
  const [myCats, setMyCats] = useState(null);
  const [filter, setFilter] = useState("mine");
  const [regate, setRegate] = useState([]);
  const [day, setDay] = useState(null);
  const [form, setForm] = useState({ open: false, regata: null });
  const months = typeof window !== "undefined" && window.innerWidth < 768 ? 1 : 2;

  useEffect(() => {
    api.get("/boats").then((r) => {
      const cats = [...new Set([...r.data.mine, ...r.data.crew].map((b) => b.categoria))];
      setMyCats(cats);
      if (!cats.length) setFilter("all");
    });
  }, []);

  const load = useCallback(async () => {
    if (myCats === null) return;
    const categorie = filter === "mine" ? myCats.join(",") : filter === "all" ? "" : filter;
    const { data } = await api.get("/regate", { params: { categorie } });
    setRegate(data);
  }, [filter, myCats]);

  useEffect(() => {
    load();
  }, [load]);

  const ranges = useMemo(() => regate.map((r) => ({ from: parseDay(r.data_inizio), to: parseDay(r.data_fine) })), [regate]);
  const today = todayStr();
  const dayStr = day && `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
  const list = dayStr ? regate.filter((r) => r.data_inizio <= dayStr && r.data_fine >= dayStr) : regate.filter((r) => r.data_fine >= today);

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la regata di ${r.luogo}?`)) return;
    try {
      await api.delete(`/regate/${r.id}`);
      toast.success("Regata eliminata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader kicker="Calendario" title="Regate">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52 bg-white" data-testid="regate-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {myCats?.length > 0 && <SelectItem value="mine">Le mie categorie</SelectItem>}
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {BOAT_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`regate-filter-option-${slug(c)}`}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {user.role === "admin" && <RegateImport onDone={load} />}
        {staff && (
          <Button onClick={() => setForm({ open: true, regata: null })} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="regata-add-button">
            <Plus className="h-4 w-4 mr-1" />Nuova regata
          </Button>
        )}
      </PageHeader>
      {filter === "mine" && myCats?.length > 0 && (
        <p className="text-sm text-muted-foreground -mt-4 mb-6">Regate per: <strong>{myCats.join(", ")}</strong></p>
      )}
      <Panel className="mb-8 flex flex-col md:flex-row gap-8 items-start fade-up">
        <Calendar mode="single" selected={day} onSelect={setDay} locale={it} numberOfMonths={months} className="mx-auto md:mx-0"
          modifiers={{ regata: ranges }} modifiersClassNames={{ regata: "day-regata" }} data-testid="regate-calendar" />
        <div className="text-sm text-muted-foreground space-y-3 md:pt-4">
          <div className="flex items-center gap-2"><span className="h-4 w-4 rounded bg-emerald-600 inline-block" />Giorni di regata</div>
          <p>Seleziona un giorno per vedere le regate in programma.</p>
          {day && <Button variant="outline" size="sm" onClick={() => setDay(null)} data-testid="regate-clear-day-button">Mostra tutte le prossime</Button>}
        </div>
      </Panel>
      <h2 className="font-heading text-2xl uppercase mb-4 flex items-center gap-2"><Flag className="h-5 w-5 text-emerald-700" />{day ? `Regate del ${fmtDate(dayStr)}` : "Prossime regate"}</h2>
      {list.length === 0 ? <Empty data-testid="regate-empty">Nessuna regata in programma.</Empty> : (
        <div className="space-y-4" data-testid="regate-list">
          {list.map((r) => <RegataCard key={r.id} r={r} staff={staff} onEdit={(x) => setForm({ open: true, regata: x })} onDelete={remove} />)}
        </div>
      )}
      <RegataForm open={form.open} regata={form.regata} onOpenChange={(o) => setForm({ ...form, open: o })} onSaved={load} />
    </div>
  );
}
